import { getCategorySlugFromText, getJobCategoryBySlug } from "./jobCategoryRegistry";
import { resolveLocationSlug } from "./locationRoutes";

export interface IntentResolutionResult {
  isStructuredIntent: boolean;
  canonicalPath: string;
  reason: string;
}

/**
 * Resolves structured search queries (e.g. "First Jobs", "Medical & Healthcare", "Sydney, NSW", "Hidden Jobs")
 * to their authoritative, clean canonical URLs.
 *
 * For arbitrary/free-text queries (e.g. "night shift bakery"), returns isStructuredIntent: false,
 * signaling that it should remain a dynamic search on /find-jobs with `noindex, follow`.
 */
export function resolveCanonicalSearchIntent(
  rawQ?: string | null,
  rawLoc?: string | null,
  rawCategory?: string | null
): IntentResolutionResult | null {
  const q = (rawQ || "").trim();
  const loc = (rawLoc || "").trim();
  const category = (rawCategory || "").trim();

  if (!q && !loc && !category) {
    return null;
  }

  const cleanQ = q.toLowerCase();
  const cleanCategory = category.toLowerCase();

  // Resolve location ONLY if it is a valid supported/state location record or explicit location param
  let locSlug: string | null = null;

  if (loc) {
    const res = resolveLocationSlug(loc);
    if (res.type === "supported" || res.type === "state" || res.locationRecord || res.stateRecord) {
      locSlug = res.normalizedSlug;
    }
  }

  // If no explicit loc param, check if q is purely a location
  if (!locSlug && q) {
    const resFromQ = resolveLocationSlug(q);
    if ((resFromQ.type === "supported" || resFromQ.type === "state" || resFromQ.locationRecord || resFromQ.stateRecord) && resFromQ.type !== "reserved") {
      locSlug = resFromQ.normalizedSlug;
    }
  }

  // 1. First Jobs intent
  const isFirstJobs =
    cleanQ.includes("first job") ||
    cleanQ.includes("first-job") ||
    cleanQ.includes("first jobs") ||
    cleanQ === "first jobs" ||
    cleanQ.includes("entry level") ||
    cleanQ.includes("teen job") ||
    cleanQ.includes("student job") ||
    cleanCategory === "first-jobs";

  if (isFirstJobs) {
    if (locSlug) {
      return {
        isStructuredIntent: true,
        canonicalPath: `/jobs/${locSlug}/entry-level`,
        reason: "Mapped structured First Jobs search with location to canonical location entry-level niche route"
      };
    }
    return {
      isStructuredIntent: true,
      canonicalPath: "/first-jobs",
      reason: "Mapped structured First Jobs search to canonical /first-jobs landing page"
    };
  }

  // 2. Hidden Jobs intent
  const isHiddenJobs =
    cleanQ.includes("hidden job") ||
    cleanQ.includes("unposted job") ||
    cleanQ.includes("unadvertised job") ||
    cleanQ === "hidden jobs" ||
    cleanCategory === "hidden-jobs";

  if (isHiddenJobs) {
    if (locSlug) {
      return {
        isStructuredIntent: true,
        canonicalPath: `/businesses-hiring/${locSlug}`,
        reason: "Mapped structured Hidden Jobs search with location to canonical businesses-hiring location route"
      };
    }
    return {
      isStructuredIntent: true,
      canonicalPath: "/hidden-jobs",
      reason: "Mapped structured Hidden Jobs search to canonical /hidden-jobs landing page"
    };
  }

  // 3. Local Internships intent
  const isInternships =
    cleanQ.includes("internship") ||
    cleanQ.includes("student internship") ||
    cleanQ === "local internships" ||
    cleanCategory === "local-internships";

  if (isInternships) {
    if (locSlug) {
      return {
        isStructuredIntent: true,
        canonicalPath: `/internships/${locSlug}`,
        reason: "Mapped structured Internship search with location to canonical internships location route"
      };
    }
    return {
      isStructuredIntent: true,
      canonicalPath: "/local-internships",
      reason: "Mapped structured Internship search to canonical /local-internships landing page"
    };
  }

  // 4. Businesses Hiring Near Me intent
  const isBusinessesHiring =
    cleanQ.includes("businesses hiring") ||
    cleanQ.includes("companies hiring") ||
    cleanQ.includes("hiring near me") ||
    cleanQ === "businesses hiring near me" ||
    cleanCategory === "businesses-hiring-near-me";

  if (isBusinessesHiring) {
    if (locSlug) {
      return {
        isStructuredIntent: true,
        canonicalPath: `/businesses-hiring/${locSlug}`,
        reason: "Mapped structured Businesses Hiring search with location to canonical businesses-hiring location route"
      };
    }
    return {
      isStructuredIntent: true,
      canonicalPath: "/businesses-hiring-near-me",
      reason: "Mapped structured Businesses Hiring search to canonical /businesses-hiring-near-me landing page"
    };
  }

  // 5. Structured Job Category intent
  const textToEvaluate = category || q;
  if (textToEvaluate) {
    const catSlug = getCategorySlugFromText(textToEvaluate);
    const validCat = getJobCategoryBySlug(catSlug);

    // Check if query is explicitly matching or close to a category
    const isCategoryMatch = validCat && (
      cleanQ.includes(validCat.slug) ||
      cleanQ.includes(validCat.name.toLowerCase()) ||
      cleanCategory.includes(validCat.slug) ||
      cleanCategory.includes(validCat.name.toLowerCase()) ||
      Boolean(category)
    );

    if (isCategoryMatch && validCat) {
      if (locSlug) {
        return {
          isStructuredIntent: true,
          canonicalPath: `/jobs/${locSlug}/${validCat.slug}`,
          reason: `Mapped structured category '${validCat.name}' + location '${locSlug}' to canonical category-location route`
        };
      }
      return {
        isStructuredIntent: true,
        canonicalPath: `/jobs/${validCat.slug}`,
        reason: `Mapped structured category '${validCat.name}' search to canonical category route`
      };
    }
  }

  // 6. Location-only search
  if (locSlug && !q && !category) {
    return {
      isStructuredIntent: true,
      canonicalPath: `/jobs/${locSlug}`,
      reason: `Mapped location-only search to canonical jobs location route`
    };
  }

  // 7. Arbitrary free-text query (e.g. "night shift bakery")
  return {
    isStructuredIntent: false,
    canonicalPath: "/find-jobs",
    reason: "Arbitrary free-text search query; remains dynamic search with noindex"
  };
}
